import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { Table, TableContainer, Tbody, Th, Thead, Tr, Td, Flex, Box, useDisclosure, HStack } from '@chakra-ui/react';
import { ChevronDown24, ChevronUp24, Icon, Info, Notification } from '@whitbread-eos/atoms';
import { TableFilter } from '@whitbread-eos/molecules';
import { formatDataTestId } from '@whitbread-eos/utils';
import { useTranslation } from 'next-i18next';
import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { TABLE_FIELDS } from './tableFields.enum';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
export default function ChangeLogTable(_ref) {
  var columns = _ref.columns,
    rows = _ref.rows;
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var _useState = useState(() => {
      return columns.filter(col => col.isFilterable).reduce((obj, item) => {
        return _objectSpread(_objectSpread({}, obj), {}, {
          [item.key]: []
        });
      }, {});
    }),
    _useState2 = _slicedToArray(_useState, 2),
    appliedFilters = _useState2[0],
    setAppliedFilters = _useState2[1];
  var columnsWithFilterData = useMemo(() => {
    return columns.map(column => {
      if (!column.isFilterable) {
        return _objectSpread({}, column);
      }
      var filterOptions = rows.reduce((acc, current) => {
        var _current$column$key;
        var value = (_current$column$key = current[column.key]) !== null && _current$column$key !== void 0 ? _current$column$key : '';
        if (value && !acc.includes(value)) {
          return [...acc, value];
        }
        return [...acc];
      }, []);
      return _objectSpread(_objectSpread({}, column), {}, {
        filterOptions
      });
    });
  }, [columns, rows]);
  var filteredRows = useMemo(() => {
    return rows.filter(row => {
      return Object.keys(appliedFilters).filter(key => {
        var _appliedFilters$key;
        return (_appliedFilters$key = appliedFilters[key]) === null || _appliedFilters$key === void 0 ? void 0 : _appliedFilters$key.length;
      }).every(key => {
        var _row$key;
        var rowValue = (_row$key = row[key]) !== null && _row$key !== void 0 ? _row$key : '';
        return rowValue && appliedFilters[key].includes(rowValue);
      });
    });
  }, [appliedFilters, rows]);
  var handleFiltersApplied = (selectedFilters, filterKey) => {
    if (appliedFilters[filterKey] !== undefined) {
      setAppliedFilters(current => {
        return _objectSpread(_objectSpread({}, current), {}, {
          [filterKey]: selectedFilters
        });
      });
    }
  };
  var filterChevron = (isPopoverOpen, columnKey) => {
    var chevronSvg = isPopoverOpen ? _jsx(ChevronUp24, {}) : _jsx(ChevronDown24, {});
    return _jsx(Box, {
      as: "button",
      "data-testid": "FilterExxpand-".concat(columnKey),
      children: _jsx(Icon, {
        svg: chevronSvg
      })
    });
  };
  var tableHeaderRow = _jsx(Tr, {
    children: columnsWithFilterData.map(column => {
      var _column$filterOptions, _appliedFilters$colum;
      return _jsx(Th, _objectSpread(_objectSpread({}, _objectSpread(_objectSpread({}, cellStyles), tableHeaderCellStyles)), {}, {
        children: _jsxs(Flex, {
          alignItems: "center",
          gap: 2,
          children: [column.title, column.isFilterable && _jsx(TableFilter, {
            popoverTrigger: filterChevron,
            filterKey: column.key,
            filterOptions: (_column$filterOptions = column.filterOptions) !== null && _column$filterOptions !== void 0 ? _column$filterOptions : [],
            selectedFilters: (_appliedFilters$colum = appliedFilters[column.key]) !== null && _appliedFilters$colum !== void 0 ? _appliedFilters$colum : [],
            onApplyFilters: handleFiltersApplied,
            externalStyles: {
              contentStyles
            }
          })]
        })
      }), column.key);
    })
  });
  var createTableCell = (column, row, rowIndex) => {
    var cellValue = column.render ? column.render(row) : row[column.key];
    return _jsx(Td, _objectSpread(_objectSpread({}, tableCellStyles(column.key)), {}, {
      children: column.clampOverflowingText ? _jsx(ExpandableText, {
        value: cellValue,
        startingHeight: 48,
        postFixtestId: "col-".concat(column.key, "-row-").concat(rowIndex)
      }) : cellValue
    }), column.key);
  };
  return _jsxs(_Fragment, {
    children: [_jsx(TableContainer, _objectSpread(_objectSpread({}, tableContainerStyles), {}, {
      children: _jsxs(Table, {
        size: "sm",
        children: [_jsx(Thead, _objectSpread(_objectSpread({}, headerTableStyle), {}, {
          children: tableHeaderRow
        })), _jsx(Tbody, {
          children: filteredRows.map((row, rowIndex) => {
            return _jsx(Tr, {
              children: columnsWithFilterData.map(column => createTableCell(column, row, rowIndex))
            }, rowIndex);
          })
        })]
      })
    })), !!rows.length && !filteredRows.length && _jsx(Notification, {
      maxWidth: "full",
      variant: "info",
      status: "info",
      description: t('ccui.changeLogModal.noResults'),
      svg: _jsx(Info, {}),
      wrapperStyles: notificationStyles
    })]
  });
}
var ExpandableText = _ref2 => {
  var value = _ref2.value,
    startingHeight = _ref2.startingHeight,
    _ref2$postFixtestId = _ref2.postFixtestId,
    postFixtestId = _ref2$postFixtestId === void 0 ? '' : _ref2$postFixtestId;
  var _useDisclosure = useDisclosure(),
    isOpen = _useDisclosure.isOpen,
    onToggle = _useDisclosure.onToggle;
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    hasOverflow = _useState4[0],
    setHasOverflow = _useState4[1];
  var _useState5 = useState(startingHeight),
    _useState6 = _slicedToArray(_useState5, 2),
    contentHeight = _useState6[0],
    setContentHeight = _useState6[1];
  var contentRef = useRef(null);
  useLayoutEffect(() => {
    var updateContentHeight = () => {
      if (contentRef.current) {
        var scrollHeight = contentRef.current.scrollHeight;
        setContentHeight(Math.min(scrollHeight, startingHeight));
        setHasOverflow(scrollHeight > startingHeight);
      }
    };
    window.addEventListener('resize', updateContentHeight);
    updateContentHeight();
    return () => {
      window.removeEventListener('resize', updateContentHeight);
    };
  }, [contentRef, startingHeight, value]);
  return _jsxs(HStack, {
    overflow: "hidden",
    height: isOpen ? 'fit-content' : "".concat(contentHeight, "px"),
    alignItems: "flex-start",
    children: [_jsx(Box, {
      ref: contentRef,
      children: value
    }), hasOverflow && _jsx(Box, {
      as: "button",
      onClick: onToggle,
      "data-testid": formatDataTestId('Expand-Collapse-Chevron', postFixtestId),
      children: _jsx(Icon, {
        svg: isOpen ? _jsx(ChevronUp24, {}) : _jsx(ChevronDown24, {})
      })
    })]
  });
};
var headerTableStyle = {
  backgroundColor: 'lightGrey5'
};
var cellStyles = {
  borderBottom: 'var(--chakra-borders-1px)',
  borderColor: 'var(--chakra-colors-lightGrey2)',
  color: 'darkGrey1',
  fontWeight: 'normal',
  fontSize: {
    mobile: 'sm',
    md: 'md'
  },
  lineHeight: {
    mobile: '2',
    sm: '3'
  },
  padding: {
    mobile: 'var(--chakra-space-10) var(--chakra-space-1) var(--chakra-space-6)',
    xs: 'var(--chakra-space-8) var(--chakra-space-1) var(--chakra-space-6)',
    sm: 'var(--chakra-space-12) var(--chakra-space-1) var(--chakra-space-6)',
    md: 'var(--chakra-space-12) var(--chakra-space-4) var(--chakra-space-6)',
    lg: 'var(--chakra-space-6) var(--chakra-space-2) var(--chakra-space-6) var(--chakra-space-6)',
    xl: 'var(--chakra-space-6) var(--chakra-space-4) var(--chakra-space-6) var(--chakra-space-6)'
  },
  verticalAlign: 'top',
  _first: {
    paddingLeft: {
      lg: 'var(--chakra-space-10)',
      xl: 'var(--chakra-space-5xl)'
    }
  },
  _last: {
    paddingRight: {
      lg: ' var(--chakra-space-10)',
      xl: ' var(--chakra-space-5xl)'
    }
  }
};
var tableCellStyles = columnKey => {
  var cellWidthStyles = columnKey === TABLE_FIELDS.ACTION_DESCRIPTION ? {
    width: "33%"
  } : {};
  return _objectSpread(_objectSpread({}, cellStyles), cellWidthStyles);
};
var tableContainerStyles = {
  whiteSpace: 'normal'
};
var tableHeaderCellStyles = {
  color: 'darkGrey1',
  fontWeight: 'semibold',
  fontSize: {
    lg: 'lg'
  },
  textTransform: 'none',
  fontFamily: 'header',
  letterSpacing: 'initial'
};
var contentStyles = {
  width: {
    mobile: 'full',
    lg: '365px'
  },
  height: {
    mobile: 'full',
    lg: '536px'
  }
};
var notificationStyles = {
  width: 'auto',
  margin: {
    mobile: 'var(--chakra-space-10) var(--chakra-space-2)',
    lg: 'var(--chakra-space-10) var(--chakra-space-10)',
    xl: 'var(--chakra-space-10) var(--chakra-space-5xl)'
  }
};